import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { 
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import { dashboardAPI, categoryAPI } from '../api/api';
import { getColorForName } from '../utils/colorUtils';

const CategoryDistributionChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch both chart data and category data
        const [chartResponse, categoryResponse] = await Promise.all([
          dashboardAPI.getCategoryDistribution(),
          categoryAPI.getCategories()
        ]);
        
        // Map the chart data to use standard names if available
        const formattedData = (chartResponse.data || []).map(item => {
          const category = (categoryResponse.data || []).find(c => 
            c.name.toLowerCase() === (item.name || '').toLowerCase()
          );
          
          // Use standard name if available, otherwise use the original name
          const displayName = category && category.standard_name && category.standard_name.trim() !== '' 
            ? category.standard_name 
            : item.name || 'Unknown';
          
          return {
            name: displayName,
            value: parseFloat(item.value) || 0
          };
        });
        
        setChartData(formattedData);
      } catch (error) {
        console.error('Error fetching category distribution data:', error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate total value for percentage calculation
  const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);

  return (
    <Card 
      elevation={0} 
      sx={{ 
        p: 3, 
        backgroundColor: 'background.paper',
        borderRadius: 3,
        height: '100%',
        boxShadow: 1
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 2 }}>
        Category Distribution
      </Typography>
      <Box sx={{ height: 350 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false} // Remove arrows pointing to the chart
                outerRadius={100}
                innerRadius={60}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                label={false} // Remove labels from slices
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getColorForName(entry.name, chartData.map(item => ({name: item.name})), index)} 
                    stroke="#fff"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => [`${value.toFixed(2)}`, 'Amount']}
                labelFormatter={(label) => `Category: ${label}`}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                align="center"
                wrapperStyle={{
                  paddingTop: '20px',
                  fontSize: '12px'
                }}
                formatter={(value, entry, index) => {
                  // Calculate percentage for each category
                  const percent = totalValue > 0 ? (entry.payload.value / totalValue) * 100 : 0;
                  return `${value} (${percent.toFixed(1)}%)`;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography variant="body2" color="text.secondary">
              No data available
            </Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default CategoryDistributionChart;